'use client'

import type { RefObject } from 'react'
import Link from 'next/link'
import { BookOpen } from 'lucide-react'
import { NOTEBOOK } from '@/lib/notebook-theme'
import { useContentModuleImpression } from '@/components/analytics/content-module-impression'

const MODULE = 'tool_related_articles'

interface RelatedArticle {
  title: string
  href: string
}

// Static mapping of tool types to relevant blog posts
// These are the highest-traffic posts per topic area from GSC data
const TOOL_ARTICLES: Record<string, RelatedArticle[]> = {
  'fertilizer': [
    { title: 'How to Calculate Fertilizer for Your Lawn', href: '/blog/how-to-calculate-fertilizer-for-lawn' },
    { title: 'Best Fertilizer for Bermuda Grass', href: '/blog/best-fertilizer-for-bermuda-grass-top-picks-reviews' },
    { title: 'How to Read Fertilizer Numbers', href: '/blog/how-to-read-fertilizer-numbers' },
  ],
  'mowing': [
    { title: 'What Professional Mowing Costs per Visit', href: '/costs#mowing' },
    { title: 'Mowing Height by Grass Type: Complete Guide', href: '/blog/mowing-height-by-grass-type-guide' },
    { title: 'How to Get Thick Grass', href: '/blog/how-to-get-thick-grass' },
    { title: 'Best Grass for High-Traffic Areas', href: '/blog/best-grass-for-hightraffic-areas' },
  ],
  'watering': [
    { title: 'How to Build a Watering Schedule That Works', href: '/blog/lawn-watering-schedule-guide' },
    { title: 'How Often to Water Based on Grass Type', href: '/blog/how-often-to-water-based-on-grass-type' },
    { title: 'Signs of Underwatering in Grass', href: '/blog/signs-of-underwatering-in-grass' },
  ],
  'herbicide': [
    { title: 'Pre-Emergent Timing: When to Apply by ZIP Code', href: '/blog/pre-emergent-timing-when-to-apply-guide' },
    { title: 'Scotts Weed and Feed Application Guide', href: '/blog/scotts-weed-and-feed-application-guide-best-products' },
    { title: 'Pre-Emergent Herbicide Timing Guide', href: '/blog/granular-pre-emergent-herbicide-timing-application-guide' },
  ],
  'aeration': [
    { title: 'What Aeration and Overseeding Cost', href: '/costs#aeration' },
    { title: 'When to Aerate Your Lawn: Timing & Regional Guide', href: '/blog/when-to-aerate-lawn-timing-guide' },
    { title: 'How to Overseed a Lawn in Fall', href: '/blog/overseeding-best-practices' },
    { title: 'How to Aerate Your Lawn the Right Way', href: '/blog/how-to-aerate-your-lawn-the-right-way' },
  ],
  'seeding': [
    { title: 'What Grass Seed and Overseeding Cost', href: '/costs#seed' },
    { title: 'Is It Too Late to Seed? Deadlines by Region', href: '/blog/is-it-too-late-to-seed-lawn-deadlines' },
    { title: 'How Long Does Grass Seed Take to Grow?', href: '/blog/growing-grass-from-seed-how-long-complete-care-guide' },
    { title: 'Fastest Growing Grass Varieties', href: '/blog/fastest-growing-grass-varieties' },
  ],
  'cost': [
    { title: 'Lawn Care Costs by Service: Price Ranges', href: '/costs' },
    { title: 'How Much Does Lawn Care Cost? 2026 Breakdown', href: '/blog/lawn-care-cost-breakdown-2026' },
    { title: 'DIY vs Professional Lawn Care', href: '/blog/how-to-choose-between-diy-and-professional-lawn-care' },
    { title: '10 DIY Tips for Affordable Lawn Care', href: '/blog/10-diy-tips-for-affordable-lawn-care' },
  ],
  'soil-temperature': [
    { title: 'Soil Temperature Guide: When to Seed & Fertilize', href: '/blog/soil-temperature-guide-seed-fertilize-timing' },
    { title: 'California Winter Lawn Care Guide', href: '/blog/california-winter-lawn-care-101-expert-guide' },
    { title: 'How to Improve Soil pH for Grass', href: '/blog/how-to-improve-soil-ph-for-grass' },
  ],
  'grass-comparison': [
    { title: 'Cool-Season vs Warm-Season Grass Guide', href: '/blog/cool-season-vs-warm-season-grass-guide' },
    { title: 'How to Identify Your Grass Type', href: '/blog/how-to-identify-your-grass-type-by-look-feel' },
    { title: 'Best Low-Maintenance Grass Types', href: '/blog/best-lowmaintenance-grass-types' },
  ],
  'lawn-size': [
    { title: 'Lawn Care Costs by Service', href: '/costs' },
    { title: 'How to Measure Your Lawn Size', href: '/blog/how-to-measure-lawn-size-guide' },
    { title: 'Essential Lawn Care Tools', href: '/blog/essential-lawn-care-tools-every-homeowner-needs' },
    { title: 'How to Start a Lawn from Scratch', href: '/blog/how-to-start-a-lawn-from-scratch-2025' },
  ],
  'pest': [
    { title: 'Natural Lawn Pest Control for Pets and Kids', href: '/blog/natural-lawn-pest-control-pet-safe-and-kid-safe-approaches' },
    { title: 'How to Apply BioAdvanced Grub Killer', href: '/blog/bioadvanced-bayer-grub-killer-instructions-apply' },
  ],
}

interface RelatedArticlesSectionProps {
  toolType: string
  variant?: 'default' | 'notebook'
}

export function RelatedArticlesSection({ toolType, variant = 'default' }: RelatedArticlesSectionProps) {
  const articles = TOOL_ARTICLES[toolType]
  const impressionRef = useContentModuleImpression<HTMLElement>(MODULE, articles?.length)
  if (!articles || articles.length === 0) return null

  if (variant === 'notebook') {
    return (
      <section
        ref={impressionRef}
        data-module={MODULE}
        className="mt-6 sm:mt-8 rounded-2xl"
        style={{
          background: NOTEBOOK.bg,
          border: `1px solid ${NOTEBOOK.rule}`,
          padding: '24px 24px 18px',
        }}
      >
        <h2
          className="text-xl sm:text-2xl mb-4"
          style={{
            fontFamily: NOTEBOOK.fontSerif,
            color: NOTEBOOK.ink,
            fontWeight: 600,
            letterSpacing: '-0.01em',
            lineHeight: 1.2,
          }}
        >
          Related articles
        </h2>
        <div className="space-y-1">
          {articles.map((article) => (
            <Link
              key={article.href}
              href={article.href}
              className="flex items-center gap-3 px-2 py-2.5 rounded-lg group transition-colors"
              style={{ color: NOTEBOOK.ink }}
            >
              <BookOpen
                className="w-4 h-4 flex-shrink-0 transition-colors"
                style={{ color: NOTEBOOK.muted2 }}
              />
              <span
                className="text-sm font-medium group-hover:underline"
                style={{ textUnderlineOffset: 3 }}
              >
                {article.title}
              </span>
            </Link>
          ))}
        </div>
      </section>
    )
  }

  return (
    <div ref={impressionRef as RefObject<HTMLDivElement | null>} data-module={MODULE} className="mt-6 sm:mt-8">
      {/* GrassIdentifierCTA removed 2026-07-07: it sat one viewport above the
          AppDownloadCTA in ToolContentFooter and both pitch grass ID — the two
          green banners competed. The app CTA (web→app funnel) wins. */}
      <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Related Articles</h2>
      <div className="space-y-2">
        {articles.map((article) => (
          <Link
            key={article.href}
            href={article.href}
            className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors group"
          >
            <BookOpen className="w-4 h-4 text-gray-400 group-hover:text-green-600 flex-shrink-0" />
            <span className="text-sm text-gray-700 group-hover:text-green-700 font-medium">{article.title}</span>
          </Link>
        ))}
      </div>
    </div>
  )
}
